import { FeedLiveItem } from '@/models/feedLive';
import { BetSelection, EventFeedType, EventPeriod, FeedWsResponseStatus, Line } from '@/constants';
import { IFeedBoardFilters, IFeedLeague, IFeedLiveApiListResponse, IFeedLiveFilters, IFeedPregameApiListResponse, IFeedTeam, IFeedTodayFilters, ILiveFeedDataAPI, IPregameFeedDataAPI } from '@/types';
import { useFeedLeagueHandler } from './useFeedLeagueHandler';
import { useFeedTeamHandler } from './useFeedTeamHandler';
import { ShallowRef } from 'vue';
import { FeedPregameItem } from '@/models/feedPregame';
import { mergeFeedCustomEventIds } from '@/helpers/feed';
import { useRoute } from 'vue-router';
import { AppError } from '@/errors/appError';
import { useAppStore } from '@/stores/app';
import { setRemoteDictionaries } from '@/helpers/dictionary';
import isEmpty from 'lodash-es/isEmpty';
import { feedService } from '@/services/feedService'

type DataType = ILiveFeedDataAPI | IPregameFeedDataAPI;

export function useFeedResponseProcessor<T extends FeedLiveItem | FeedPregameItem>(
    eventFeedType: EventFeedType,
    items: ShallowRef<T[]>,
    filters: IFeedLiveFilters | IFeedTodayFilters | IFeedBoardFilters,
    //itemClass: new (periodTypeId: number, data: any) => T,
    itemClass: string,
    eventsPositionIdx: Ref<Record<string, number>>,
    version: Ref<number>,
    leagueItems: Ref<Record<number, IFeedLeague>>,
    teamItems: Ref<Record<number, IFeedTeam>>
) {
    const { clearFeedLeague, addFeedLeague, removeFeedLeague } = useFeedLeagueHandler(leagueItems);
    const { clearFeedTeam, addFeedTeam, removeFeedTeam } = useFeedTeamHandler(teamItems);

    const route = useRoute();
    const appStore = useAppStore();

    function checkResponse(data: IFeedLiveApiListResponse | IFeedPregameApiListResponse): FeedWsResponseStatus {
        const customEventIds = mergeFeedCustomEventIds(filters.customEventId, appStore.bettingCustomEventIds).join(',');

        //feedSubscribeId is not ready on the server yet
        /*if (data.id !== appStore.feedSubscribeId) {
            console.log('Wrong feedSubscribeId');
            return FeedWsResponseStatus.SubscribeIdIncorrect;
        }*/

        if (
            data.periodTypeId === filters.periodTypeId &&
            data.tradingTypeId === route.meta.tradingTypeId &&
            data.line === filters.line &&
            data.customEventIds === customEventIds &&
            (!('eventFeedTypeId' in filters) || data.marketId === filters.eventFeedTypeId) // eventFeedTypeId is absent in Board page
        ) {
            //if versionPrev is 0, it is ini data, need to accept anyway
            if (data.versionPrev !== 0 && data.versionPrev !== version.value) {
                //it may happen if user swith between live-board, need to wait for correct version (with versionPrev 0)
                if (version.value === 0) return FeedWsResponseStatus.WaitingCorrectVersion;

                console.log('Our current feed version %s is not equal to previous %s from WS.', version.value, data.versionPrev);
                return FeedWsResponseStatus.VersionIncorrect;
            }

            return FeedWsResponseStatus.Ok;
        } else {
            console.log(
                'Page feed tradingTypeId is %s, periodTypeId %s, eventFeedType %s, line %s, ceId %s but WS response for feed tradingTypeId %s, periodTypeId %s, eventFeedType %s, line %s, ceId %s',
                route.meta.tradingTypeId,
                filters.periodTypeId,
                'eventFeedTypeId' in filters ? filters.eventFeedTypeId : data.marketId, // eventFeedTypeId is absent in Board page
                filters.line,
                customEventIds,
                data.tradingTypeId,
                data.periodTypeId,
                data.line,
                data.marketId,
                data.customEventIds
            );
            return FeedWsResponseStatus.FeedParamsIncorrect;
        }
    }

    function processResponse(data: IFeedLiveApiListResponse | IFeedPregameApiListResponse): void {
        if (data.dictionary !== undefined) {
            setRemoteDictionaries(data.dictionary);
        }

        if (data.init !== undefined) {
            processInitResponse(data.periodTypeId, data.init);
        } else {
            if (data.update !== undefined) processUpdateResponse(data.update);
            if (data.delete !== undefined) processDeleteResponse(data.delete);
            if (data.insert !== undefined) processInsertResponse(data.periodTypeId, data.insert);
        }

        if (data.gamesIdx !== undefined) {
            eventsPositionIdx.value = data.gamesIdx;
        }

        if (data.version !== undefined) {
            version.value = data.version;
        }

        updateBetDialog();
    }

    function processInitResponse(periodTypeId: EventPeriod, data: DataType[]): void {
        clearFeedLeague();
        clearFeedTeam();

        const result: T[] = [];
        for (let i = 0; i < data.length; i++) {
            //const lineInit = new itemClass(periodTypeId, data[i]);
            let lineInit;
            if (itemClass === 'FeedLiveItem') lineInit = new FeedLiveItem(periodTypeId, data[i]);
            else lineInit = new FeedPregameItem(periodTypeId, data[i]);

            result.push(lineInit as T);

            if (lineInit.line.value === Line.Main) {
                addFeedLeague(lineInit);
                addFeedTeam(lineInit);
            }
        }

        items.value = result;
    }

    function processUpdateResponse(data: Record<string, Partial<DataType>>): void {
        for (const key in data) {
            const [customEventId, lineStr] = key.split('|');
            const line = parseInt(lineStr);
            const position = eventsPositionIdx.value[customEventId] + line - 1;
            const feedItem = items.value[position];
            if (!feedItem) {
                throw new AppError(`Updated index ${position} (event #${customEventId}) is absent`);
            }

            if (customEventId !== feedItem.customEventId.value) {
                //it happens when custom leagues for game was changed
                console.error(`Need to update ${customEventId} event, but select event ${feedItem.customEventId.value} (position ${position}) from items`);
                feedService.wsSubscribe(true);
                return;
            }

            feedItem.update(data[key]);
        }
    }

    function processInsertResponse(periodTypeId: EventPeriod, data: Record<string, DataType>): void {
        for (const insertIdx in data) {
            //const newFeedLine = new itemClass(periodTypeId, data[insertIdx]);
            let newFeedLine;
            if (itemClass === 'FeedLiveItem') newFeedLine = new FeedLiveItem(periodTypeId, data[insertIdx]);
            else newFeedLine = new FeedPregameItem(periodTypeId, data[insertIdx]);
            const copy = [...items.value];
            copy.splice(Number(insertIdx), 0, newFeedLine as T);
            items.value = copy;

            if (newFeedLine.line.value === Line.Main) {
                addFeedLeague(newFeedLine);
                addFeedTeam(newFeedLine);
            }
        }
    }

    function processDeleteResponse(data: string[]): void {
        let removed = 0;
        for (let i = 0; i < data.length; i++) {
            const [customEventId, lineStr] = data[i].split('|');
            const positionIdxToDelete = eventsPositionIdx.value[customEventId] + parseInt(lineStr) - 1 - removed;
            const deleteFeedLine = items.value[positionIdxToDelete];

            if (deleteFeedLine.line.value === Line.Main) {
                removeFeedLeague(deleteFeedLine);
                removeFeedTeam(deleteFeedLine);
            }

            items.value.splice(positionIdxToDelete, 1);
            items.value = [...items.value];

            removed++;
        }
    }

    function updateBetDialog() {
        //if was opened bet popup need to update data
        if (isEmpty(appStore.betFeedData)) {
            return;
        }

        for (let index in appStore.betFeedData) {
            let betFeedData = appStore.betFeedData[index];

            if (betFeedData.isCreatedFromFeed !== true) {
                //need to skip bettingData created from Event popup
                continue;
            }

            const feedLine = items.value.find((feedItem) => {
                if (feedItem.customEventId.value !== betFeedData.customEventId) {
                    return false;
                }

                const notHandicapSelection = betFeedData.betSelectionId !== BetSelection.HandicapHome && betFeedData.betSelectionId !== BetSelection.HandicapAway;

                let handicap = null;

                if (betFeedData.betSelectionId === BetSelection.HandicapHome || betFeedData.betSelectionId === BetSelection.HandicapAway) {
                    handicap = feedItem.handicap.value;
                } else if (betFeedData.betSelectionId === BetSelection.GoalOver || betFeedData.betSelectionId === BetSelection.GoalUnder) {
                    handicap = feedItem.goalHandicap.value;
                }

                const zeroHandicap = betFeedData.handicap === '0.00';

                const sameJollywhere = feedItem.jollywhere.value === betFeedData.jollywhere;

                if (!(notHandicapSelection || zeroHandicap || sameJollywhere)) return false;

                return betFeedData.handicap === null || handicap === betFeedData.handicap;
            });

            if (!feedLine) {
                if (eventFeedType === betFeedData.eventFeedType) {
                    //set inactive if feedType = betFeedData.feedType
                    betFeedData.isActive = false;
                }

                continue;
            }

            if (betFeedData.customEventId !== feedLine.customEventId.value) {
                console.log(`CustomEventId in feed ${feedLine.customEventId.value} and betPopup ${betFeedData.customEventId} do not match`);
                continue;
            }

            if (feedLine.getMarketDataByBetSelection(betFeedData.betSelectionId).price === null) {
                betFeedData.isActive = false;
                continue;
            }

            betFeedData.isActive = true;

            const feedMainLine =
                feedLine.line.value === Line.Main ? feedLine : items.value.find((feedItem) => feedItem.customEventId.value === betFeedData.customEventId && feedItem.line.value === Line.Main)!;

            betFeedData.eventStartedAt = feedMainLine.eventStartedAt.value!;
            betFeedData.eventFeedType = feedMainLine.eventFeedType.value!;

            if (feedMainLine instanceof FeedLiveItem) {
                betFeedData.scoreHome = feedMainLine.scoreHome.value;
                betFeedData.scoreAway = feedMainLine.scoreAway.value;

                betFeedData.cornersHome = feedMainLine.cornersHome.value;
                betFeedData.cornersAway = feedMainLine.cornersAway.value;

                betFeedData.time = feedMainLine.time.value;
            }

            betFeedData.updatePriceDataFromFeed(feedLine);
        }
    }

    return {
        checkResponse,
        processResponse,
    };
}

export type Processors = ReturnType<typeof useFeedResponseProcessor>;
